import React, { useState, useEffect, useRef } from 'react';
import { useAnalytics } from '../hooks/useAnalytics';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  IconButton, 
  Stack,
  Box,
  Chip,
  Fade
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useColorScheme } from '@mui/material/styles';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import LightModeIcon from '@mui/icons-material/LightMode';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import GitHubIcon from '@mui/icons-material/GitHub';
import LinkedInIcon from '@mui/icons-material/LinkedIn';

//

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'transparent',
  boxShadow: 'none',
  color: theme.palette.text.primary,
  pointerEvents: 'none',
}));

// Floating wrapper to mimic pill navbar centered at top
const FloatingWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 10,
  left: '50%',
  transform: 'translateX(-50%)',
  zIndex: 1200,
  width: 'min(82%, 760px)',
  borderRadius: 14,
  padding: '0 4px',
  overflow: 'visible',
  background: theme.palette.mode === 'dark'
    ? 'rgba(26,32,46,0.72)'
    : 'rgba(255,255,255,0.82)',
  backdropFilter: 'blur(14px) saturate(160%)',
  WebkitBackdropFilter: 'blur(14px) saturate(160%)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 28px rgba(0,0,0,0.35)'
    : '0 10px 28px rgba(0,102,204,0.12)',
  pointerEvents: 'auto',
}));

// Secondary right-side header (Header-2)
const RightDockWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  top: 10,
  right: 10,
  zIndex: 1200,
  borderRadius: 14,
  padding: '4px 8px',
  background: theme.palette.mode === 'dark' 
    ? 'rgba(26,32,46,0.72)'
    : 'rgba(255,255,255,0.82)',
  backdropFilter: 'blur(14px) saturate(160%)',
  WebkitBackdropFilter: 'blur(14px) saturate(160%)',
  border: theme.palette.mode === 'dark'
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(0,0,0,0.06)',
  boxShadow: theme.palette.mode === 'dark'
    ? '0 10px 28px rgba(0,0,0,0.35)'
    : '0 10px 28px rgba(0,102,204,0.12)',
  pointerEvents: 'auto',
}));

// Logo with modern effects
const Logo = styled(Typography)(() => ({
  fontFamily: '"Space Grotesk", sans-serif',
  fontWeight: 800,
  fontSize: '1.3rem',
  background: 'linear-gradient(135deg, #0066cc 0%, #00d4aa 50%, #667eea 100%)',
  backgroundSize: '200% 100%',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  cursor: 'pointer',
  position: 'relative',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  padding: '0 6px',
  borderRadius: 8,
  '&:hover': {
    backgroundPosition: '100% 0',
    transform: 'translateY(-1px)',
    filter: 'drop-shadow(0 4px 8px rgba(0, 102, 204, 0.3))'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    width: '0%',
    height: '2px',
    background: 'linear-gradient(90deg, #0066cc, #00d4aa)',
    borderRadius: '1px',
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%)'
  },
  '&:hover::after': {
    width: '100%'
  }
}));

// Modern nav button with morphing effects
const NavButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'active'
})<{ active?: boolean }>(({ theme, active }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderRadius: '10px',
  padding: '4px 10px',
  minHeight: 0,
  lineHeight: 1.2,
  margin: '0 2px',
  position: 'relative',
  color: active ? theme.palette.primary.main : theme.palette.text.primary,
  background: active 
    ? theme.palette.mode === 'dark' 
      ? 'rgba(0, 102, 204, 0.06)' 
      : 'rgba(0, 102, 204, 0.035)'
    : 'transparent',
  border: active ? `1px solid ${theme.palette.primary.main}30` : '1px solid transparent',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: `linear-gradient(90deg, transparent, ${theme.palette.primary.main}14, transparent)`,
    transition: 'left 0.45s ease'
  },
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}12, ${theme.palette.secondary.main}08)`,
    color: theme.palette.primary.main,
    transform: 'translateY(-1px) scale(1.005)',
    boxShadow: `0 6px 16px ${theme.palette.primary.main}18`,
    border: `1px solid ${theme.palette.primary.main}40`,
    '&::before': {
      left: '100%'
    }
  },
  '&:active': {
    transform: 'translateY(0) scale(0.99)'
  }
}));

// Contact CTA Button - minimal style to match NavButton
const CTAButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.78rem',
  borderRadius: '10px',
  padding: '4px 10px',
  minHeight: 0,
  lineHeight: 1.2,
  color: theme.palette.text.primary,
  background: 'transparent',
  border: '1px solid transparent',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow: 'none',
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.main}12, ${theme.palette.secondary.main}08)`,
    color: theme.palette.primary.main,
    transform: 'translateY(-1px) scale(1.005)',
    boxShadow: `0 6px 16px ${theme.palette.primary.main}18`,
    border: `1px solid ${theme.palette.primary.main}40`,
  },
  '&:active': {
    transform: 'translateY(0) scale(0.99)'
  },
  '& .MuiButton-startIcon': {
    marginRight: '8px',
    transition: 'transform 0.3s ease'
  },
  '&:hover .MuiButton-startIcon': {
    transform: 'rotate(360deg)'
  }
}));

// Modern hamburger menu button
const ModernMenuButton = styled(IconButton)(({ theme }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '12px',
  border: `1px solid ${theme.palette.divider}`,
  background: theme.palette.mode === 'dark' 
    ? 'rgba(255, 255, 255, 0.05)' 
    : 'rgba(0, 0, 0, 0.02)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    transform: 'scale(1.05)',
    boxShadow: `0 8px 25px ${theme.palette.primary.main}30`
  }
}));

// Theme toggle with enhanced animation
const ThemeToggle = styled(IconButton)(({ theme }) => ({
  width: '36px',
  height: '36px',
  borderRadius: '18px',
  background: theme.palette.mode === 'dark' 
    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
    : 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '50%',
    left: '50%',
    width: '100%',
    height: '100%',
    background: 'radial-gradient(circle, rgba(255,255,255,0.2) 0%, transparent 70%)',
    borderRadius: '50%',
    transform: 'translate(-50%, -50%) scale(0)',
    transition: 'transform 0.4s ease'
  },
  '&:hover': {
    transform: 'rotate(360deg) scale(1.1)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.3)',
    '&::before': {
      transform: 'translate(-50%, -50%) scale(1.5)'
    }
  }
}));

interface HideOnScrollProps { children: React.ReactElement }
function HideOnScroll({ children }: HideOnScrollProps) { return children; }

const Navigation: React.FC = () => {
  const { mode, setMode } = useColorScheme();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('hero');
  
  // Refs to align header centers
  const floatingRef = useRef<HTMLDivElement | null>(null);
  const rightDockRef = useRef<HTMLDivElement | null>(null);
  const [rightDockTop, setRightDockTop] = useState<number>(10);

  // Analytics tracking
  const analytics = useAnalytics();

  //

  // Track active section based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'about', 'projects', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Call once on mount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Align Header-2 center with Header-1 center
  useEffect(() => {
    const updateAlignment = () => {
      const f = floatingRef.current;
      const r = rightDockRef.current;
      if (!f || !r) return;
      const fb = f.getBoundingClientRect();
      const rb = r.getBoundingClientRect();
      const desiredTop = Math.round(fb.top + fb.height / 2 - rb.height / 2);
      setRightDockTop(desiredTop);
    };
    updateAlignment();
    window.addEventListener('resize', updateAlignment);
    return () => window.removeEventListener('resize', updateAlignment);
  }, [mode, mobileMenuOpen, activeSection]);

  const handleThemeToggle = () => {
    const newMode = mode === 'light' ? 'dark' : 'light';
    setMode(newMode);
    
    // Track theme change
    analytics.trackCustomEvent('theme_toggle', {
      from_mode: mode,
      to_mode: newMode,
      event_category: 'ui_interaction'
    });
  };

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
      
      // Track navigation
      analytics.trackNavigation(sectionId);
    }
  };

  const navItems = [
    { label: 'Ana Sayfa', id: 'hero', icon: '🏠' },
    { label: 'Hakkımda', id: 'about', icon: '👤' },
    { label: 'Projeler', id: 'projects', icon: '💼' },
    { label: 'İletişim', id: 'contact', icon: '📧' }
  ];

  return (
    <HideOnScroll>
      <StyledAppBar position="fixed" color="transparent" elevation={0}>
        <FloatingWrapper sx={{ overflow: 'visible' }} ref={floatingRef}>
          <Toolbar sx={{ padding: 0, minHeight: '2px' }}>
            <Box
              sx={{
                width: '100%',
                display: 'grid',
                gridTemplateColumns: '1fr auto 1fr',
                alignItems: 'center',
                columnGap: 1,
              }}
            >
              {/* Left Nav: Ana Sayfa & Hakkımda */}
              <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' }, gridColumn: '1 / 2', justifyContent: 'flex-start', pl: 0.5 }}>
                {[navItems[0], navItems[1]].map((item) => (
              <NavButton
                key={item.id}
                    active={activeSection === item.id}
                onClick={() => scrollToSection(item.id)}
                    startIcon={<span style={{ fontSize: '16px' }}>{item.icon}</span>}
              >
                {item.label}
              </NavButton>
            ))}
          </Stack>
          
              {/* Centered Logo with badge below; allowed to overflow */}
              <Box
                sx={{
                  gridColumn: '2 / 3',
                  justifySelf: 'center',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: 0.5,
                  pointerEvents: 'auto',
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -10px)',
                  py: 0,
                  zIndex: 2,
                }}
              >
                <Logo variant="h5" onClick={() => scrollToSection('hero')} sx={{ position: 'relative', top: '-24px', fontSize: { xs: '1.5rem', md: '2rem' } }}>
                  Mert Dalkır
                </Logo>
                <Chip
                  label="AI / Web3"
                  size="small"
                  sx={{
                    background: 'linear-gradient(45deg, #667eea, #764ba2)',
                    color: 'white',
                    fontWeight: 600,
                    mt: '-10px',
                    position: 'relative',
                    top: '-17px',
                  }}
                />
              </Box>

              {/* Right Nav: Projeler & İletişim */}
              <Stack direction="row" alignItems="center" spacing={1.25} sx={{ gridColumn: '3 / 4', justifySelf: 'end', pr: 0.5 }}>
                <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', md: 'flex' } }}>
                  {[navItems[2]].map((item) => (
                    <NavButton
                      key={item.id}
                      active={activeSection === item.id}
                      onClick={() => scrollToSection(item.id)}
                      startIcon={<span style={{ fontSize: '16px' }}>{item.icon}</span>}
                    >
                      {item.label}
                    </NavButton>
                  ))}
                  <CTAButton 
                    startIcon={<span style={{ fontSize: '16px' }}>📧</span>} 
                    onClick={() => scrollToSection('contact')}
                    sx={{
                      color: activeSection === 'contact' ? 'primary.main' : undefined,
                      background: activeSection === 'contact' 
                        ? (theme) => theme.palette.mode === 'dark' 
                          ? 'rgba(0, 102, 204, 0.06)' 
                          : 'rgba(0, 102, 204, 0.035)'
                        : 'transparent',
                      border: (theme) => activeSection === 'contact' 
                        ? `1px solid ${theme.palette.primary.main}30` 
                        : '1px solid transparent',
                    }}
                  >
                    İletişim
                  </CTAButton>
                </Stack>

                {/* Mobile Menu Button */}
                <ModernMenuButton sx={{ display: { xs: 'flex', md: 'none' } }} onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
                  {mobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                </ModernMenuButton>
              </Stack>
            </Box>
          </Toolbar>
        </FloatingWrapper>

        {/* Header-2: Only desktop and above */}
        <RightDockWrapper ref={rightDockRef} sx={{ display: { xs: 'none', md: 'flex' }, position: 'fixed', top: rightDockTop }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton 
              size="small"
              onClick={() => window.open('https://github.com/mertdalkir', '_blank')}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', transform: 'translateY(-2px)' } }}
            >
              <GitHubIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => window.open('https://linkedin.com/in/mertdalkir', '_blank')}
              sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main', transform: 'translateY(-2px)' } }}
            >
              <LinkedInIcon fontSize="small" />
            </IconButton>
            <ThemeToggle onClick={handleThemeToggle}>
              {mode === 'light' ? <DarkModeIcon /> : <LightModeIcon />}
            </ThemeToggle>
          </Stack>
        </RightDockWrapper>
        
        {/* Enhanced Mobile Navigation */}
        <Fade in={mobileMenuOpen}>
          <Box 
            sx={{ 
              display: { xs: mobileMenuOpen ? 'block' : 'none', md: 'none' },
              background: (theme) => theme.palette.mode === 'dark' 
                ? 'rgba(26, 32, 46, 0.95)' 
                : 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(20px) saturate(180%)',
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
              p: 3,
              mt: '72px',
              mx: 'auto',
              width: 'min(92%, 1100px)',
              borderRadius: 20,
            }}
          >
            <Stack spacing={2}>
              {navItems.map((item) => (
                <Button
                  key={item.id}
                  fullWidth
                  startIcon={<span style={{ fontSize: '18px' }}>{item.icon}</span>}
                  onClick={() => scrollToSection(item.id)}
                  sx={{ 
                    justifyContent: 'flex-start',
                    textTransform: 'none',
                    borderRadius: '16px',
                    padding: '12px 20px',
                    fontSize: '1.1rem',
                    fontWeight: 600,
                    color: activeSection === item.id ? 'primary.main' : 'text.primary',
                    background: activeSection === item.id 
                      ? (theme) => theme.palette.mode === 'dark' 
                        ? 'rgba(0, 102, 204, 0.1)' 
                        : 'rgba(0, 102, 204, 0.05)'
                      : 'transparent',
                    border: (theme) => activeSection === item.id 
                      ? `1px solid ${theme.palette.primary.main}40` 
                      : '1px solid transparent',
                    '&:hover': {
                      background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.secondary.main}10)`,
                      transform: 'translateX(8px)'
                    }
                  }}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Mobile Social Links */}
              <Box sx={{ pt: 2, borderTop: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={2} justifyContent="center">
                  <IconButton color="primary">
                    <GitHubIcon />
                  </IconButton>
                  <IconButton color="primary">
                    <LinkedInIcon />
                  </IconButton>
                  <IconButton color="primary">
                    <EmailIcon />
                  </IconButton>
                </Stack>
              </Box>
            </Stack>
          </Box>
        </Fade>
      </StyledAppBar>
    </HideOnScroll>
  );
};

export default Navigation;